import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import type { Project } from "@/lib/api";

export default function ProjectCard({ project }: { project: Project }) {
  const inExecution = project.stage === "in_execution" || !project.reality_url;
  return (
    <Link
      to={`/portfolio/${project.slug}`}
      data-testid={`project-card-${project.slug}`}
      className="group relative block h-full w-full overflow-hidden bg-obsidian-card"
    >
      <div className="relative h-full w-full">
        <img
          src={project.render_url}
          alt={`${project.title} — approved 3D render`}
          loading="lazy"
          className={`absolute inset-0 h-full w-full object-cover transition-transform duration-700 ${
            inExecution ? "group-hover:scale-[1.03]" : "transition-opacity group-hover:opacity-0"
          }`}
        />
        {!inExecution && (
          <img
            src={project.reality_url}
            alt={`${project.title} — finished reality photo`}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-700 group-hover:opacity-100"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/10 to-transparent" />

        {inExecution ? (
          <span className="absolute left-4 top-4 flex items-center gap-2 bg-obsidian/70 px-3 py-1.5 font-display text-[10px] font-semibold uppercase tracking-[0.18em] text-blue-300 backdrop-blur-sm" data-testid={`project-card-stage-${project.slug}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-blue-400" />
            Approved 3D · Photos to follow
          </span>
        ) : (
          <>
            <span className="absolute left-4 top-4 flex items-center gap-2 bg-obsidian/70 px-3 py-1.5 font-display text-[10px] font-semibold uppercase tracking-[0.18em] text-blue-300 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-0">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-400" />
              3D · Render
            </span>
            <span className="absolute left-4 top-4 flex items-center gap-2 bg-obsidian/70 px-3 py-1.5 font-display text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-300 opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              Reality
            </span>
          </>
        )}

        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6">
          <div>
            {project.is_real && !inExecution && (
              <span className="mb-3 inline-block border border-gold/60 px-2.5 py-1 font-display text-[9px] font-semibold uppercase tracking-[0.2em] text-gold" data-testid={`project-card-real-badge-${project.slug}`}>
                Delivered · site photos
              </span>
            )}
            <p className="font-display text-[10px] uppercase tracking-[0.2em] text-gold">
              {project.city} · {project.budget_label} · {project.property_type}
            </p>
            <h3 className="mt-2 font-serif text-2xl text-zinc-50 md:text-3xl">{project.title}</h3>
          </div>
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/25 text-zinc-100 transition-all duration-300 group-hover:border-gold group-hover:bg-gold group-hover:text-obsidian">
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}
